import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, Dropdown, Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { AiOutlineClose } from 'react-icons/ai';
import { RiFileExcel2Line } from 'react-icons/ri';
import * as XLSX from 'xlsx';
import { LoaderContext } from '../../../App';
import {
  CompareCategoryType,
  DataViewCategoryType,
  LS_REGULATION_SELECTED_KEY,
  StandardCategory,
} from '../../../core/constants/common';
import CompanyService from '../../../core/service/CompanyService';
import HomePageService from '../../../core/service/HomePageService';
import StandardService from '../../../core/service/StandardService';
import './UnreportedDataPointsHome.scss';

const PERIOD_TYPE = {
  YEARLY: 'YEARLY',
  QUARTERLY: 'QUARTERLY',
  MONTHLY: 'MONTHLY',
};

const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const QUARTER_CODES = ['Q1', 'Q2', 'Q3', 'Q4'];

/**
 * Years shown in Reporting period: newest first, starting at calendar current year + 1.
 * @param {number} currentYear - typically `new Date().getFullYear()`
 * @param {number} count - how many fiscal years to list (default 7)
 */
function buildReportingPeriodYearList(currentYear, count = 7) {
  const topYear = currentYear + 1;
  const years = [];
  for (let i = 0; i < count; i += 1) {
    years.push(topYear - i);
  }
  return years;
}

function defaultPeriodFromFilter(defaultPeriodFilter) {
  if (!defaultPeriodFilter) {
    const y = new Date().getFullYear();
    return { periodType: PERIOD_TYPE.YEARLY, period: { [String(y)]: [] } };
  }
  const s = String(defaultPeriodFilter).trim().slice(0, 10);
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (isoDay) {
    const y = Number(isoDay[1]);
    const monthIndex = parseInt(isoDay[2], 10) - 1;
    if (Number.isFinite(y) && monthIndex >= 0 && monthIndex <= 11) {
      return {
        periodType: PERIOD_TYPE.MONTHLY,
        period: { [String(y)]: [MONTH_CODES[monthIndex]] },
      };
    }
  }
  const d = new Date(defaultPeriodFilter);
  if (Number.isNaN(d.getTime())) {
    const y = new Date().getFullYear();
    return { periodType: PERIOD_TYPE.YEARLY, period: { [String(y)]: [] } };
  }
  const y = d.getFullYear();
  const m = d.getMonth();
  return {
    periodType: PERIOD_TYPE.MONTHLY,
    period: { [String(y)]: [MONTH_CODES[m]] },
  };
}

function isReportingPeriodValid(periodType, period) {
  const p = period || {};
  const keys = Object.keys(p);
  if (keys.length === 0) return false;
  if (periodType === PERIOD_TYPE.YEARLY) return true;
  return keys.some((k) => (p[k] || []).length > 0);
}

function countReportingSelections(periodType, period) {
  const p = period || {};
  if (periodType === PERIOD_TYPE.YEARLY) {
    return Object.keys(p).length;
  }
  return Object.values(p).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
}

function formatUnansweredPeriodLabel(isoDate, tMonth) {
  if (!isoDate || typeof isoDate !== 'string') return '';
  const s = isoDate.trim().slice(0, 10);
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (parsed) {
    const y = Number(parsed[1]);
    const monthIndex = parseInt(parsed[2], 10) - 1;
    if (Number.isFinite(y) && monthIndex >= 0 && monthIndex <= 11) {
      return `${tMonth(MONTH_CODES[monthIndex])} ${y}`;
    }
  }
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  const y = d.getFullYear();
  const code = MONTH_CODES[d.getMonth()];
  return `${tMonth(code)} ${y}`;
}

function flattenEnterpriseTree(nodes, parentId = null) {
  const rows = [];
  (nodes || []).forEach((n) => {
    rows.push({ id: n.id, name: n.name, parentId });
    if (n.children?.length) {
      rows.push(...flattenEnterpriseTree(n.children, n.id));
    }
  });
  return rows;
}

const defaultStandardId = () => {
  const raw = localStorage.getItem(LS_REGULATION_SELECTED_KEY);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : '';
};

const normId = (id) => Number(id);

function normalizeEntitySearch(s) {
  return String(s || '')
    .trim()
    .toLowerCase();
}

/**
 * Home page entry: opens a fullscreen modal to list unanswered data points per child branch.
 */
const UnreportedDataPointsHome = ({ t, defaultPeriodFilter }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [showModal, setShowModal] = useState(false);
  const [topics, setTopics] = useState([]);
  const [parentEntityRows, setParentEntityRows] = useState([]);
  const [childBranchRows, setChildBranchRows] = useState([]);
  const [results, setResults] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [showTopicsDropdown, setShowTopicsDropdown] = useState(false);
  const [showChildDropdown, setShowChildDropdown] = useState(false);
  const [showReportingPeriodDropdown, setShowReportingPeriodDropdown] = useState(false);
  const [parentEntitySearch, setParentEntitySearch] = useState('');
  const [childBranchSearch, setChildBranchSearch] = useState('');

  const fiscalYearOptions = useMemo(() => buildReportingPeriodYearList(new Date().getFullYear(), 7), []);

  const [form, setForm] = useState(() => {
    const pr = defaultPeriodFromFilter(defaultPeriodFilter);
    return {
      categoryType: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      childBranchIds: [],
      parentEntityIds: [],
      periodType: pr.periodType,
      period: pr.period,
      unanswered: true,
    };
  });

  const categoryOptions = useMemo(
    () => [
      { value: DataViewCategoryType.ENVIRONMENTAL, label: t('Environmental.label') },
      { value: DataViewCategoryType.SOCIAL, label: t('Social.label') },
      { value: DataViewCategoryType.GOVERNANCE, label: t('Governance.label') },
    ],
    [t]
  );

  const loadEnterpriseStructure = useCallback(() => {
    CompanyService.getEnterpriseStructureMin()
      .then((res) => {
        if (res.status !== 200) return;
        const data = res.data?.data || res.data || [];
        const flat = flattenEnterpriseTree(data);
        setParentEntityRows(flat.filter((r) => r.parentId === null));
        setChildBranchRows(flat.filter((r) => r.parentId !== null));
      })
      .catch(() => {
        setParentEntityRows([]);
        setChildBranchRows([]);
      });
  }, []);

  const loadTopicsForForm = useCallback((standardId, categoryType) => {
    if (!standardId) {
      setTopics([]);
      return;
    }
    StandardService.getTopicsByStandard(standardId, false, StandardCategory.QUANTITATIVE, categoryType)
      .then((res) => {
        if (res.status !== 200) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setTopics(
          list.map((topic) => ({
            ...topic,
            name: topic.name || topic.topicName || `Topic ${topic.id}`,
          }))
        );
      })
      .catch(() => setTopics([]));
  }, []);

  const sid = defaultStandardId();

  const closeAllDropdowns = () => {
    setShowParentDropdown(false);
    setShowTopicsDropdown(false);
    setShowChildDropdown(false);
    setShowReportingPeriodDropdown(false);
  };

  const openModal = () => {
    closeAllDropdowns();
    const pr = defaultPeriodFromFilter(defaultPeriodFilter);
    setForm({
      categoryType: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      childBranchIds: [],
      parentEntityIds: [],
      periodType: pr.periodType,
      period: pr.period,
      unanswered: true,
    });
    setResults([]);
    setFetchError('');
    setParentEntitySearch('');
    setChildBranchSearch('');
    setShowModal(true);
    loadEnterpriseStructure();
  };

  useEffect(() => {
    if (!showModal) return;
    loadTopicsForForm(sid, form.categoryType);
  }, [showModal, sid, form.categoryType, loadTopicsForForm]);

  useEffect(() => {
    if (showModal) return;
    const pr = defaultPeriodFromFilter(defaultPeriodFilter);
    setForm((p) => ({ ...p, periodType: pr.periodType, period: pr.period }));
  }, [defaultPeriodFilter, showModal]);

  const handleReset = () => {
    closeAllDropdowns();
    const pr = defaultPeriodFromFilter(defaultPeriodFilter);
    setForm({
      categoryType: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      childBranchIds: [],
      parentEntityIds: [],
      periodType: pr.periodType,
      period: pr.period,
      unanswered: true,
    });
    setResults([]);
    setFetchError('');
    setParentEntitySearch('');
    setChildBranchSearch('');
    loadTopicsForForm(defaultStandardId(), DataViewCategoryType.ENVIRONMENTAL);
  };

  const handleApply = () => {
    const standardId = defaultStandardId();
    if (!standardId) {
      setFetchError(t('UnreportedDataPointsHome.validation.regulation.label'));
      return;
    }
    if (!isReportingPeriodValid(form.periodType, form.period)) {
      setFetchError(t('UnreportedDataPointsHome.validation.reportingPeriod.label'));
      return;
    }
    if (!form.childBranchIds?.length) {
      setFetchError(t('UnreportedDataPointsHome.validation.childBranches.label'));
      return;
    }

    const category = CompareCategoryType[form.categoryType] || 'ENVIRONMENTAL';
    const periodPayload = JSON.parse(JSON.stringify(form.period || {}));
    const payload = {
      standardId,
      category,
      topicIds: form.topicIds?.length ? [...form.topicIds] : [],
      childBranchIds: [...form.childBranchIds],
      parentEntities: form.parentEntityIds?.length ? [...form.parentEntityIds] : [],
      periodType: form.periodType,
      period: periodPayload,
      unanswered: Boolean(form.unanswered),
    };

    setFetchError('');
    setLoaderDisplay(true);
    HomePageService.getBranchUnansweredDataPoints(payload)
      .then((res) => {
        setLoaderDisplay(false);
        const body = res.data;
        const list = Array.isArray(body) ? body : body?.data ?? [];
        setResults(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setLoaderDisplay(false);
        setResults([]);
        setFetchError(t('UnreportedDataPointsHome.error.label'));
      });
  };

  const handleDownloadExcel = () => {
    if (!results.length) return;
    const periodLabel = (iso) =>
      formatUnansweredPeriodLabel(iso, (c) => t(`UnreportedDataPointsHome.month.${c}.label`));
    const headers = [
      t('UnreportedDataPointsHome.datapointCode.label'),
      t('UnreportedDataPointsHome.datapointName.label'),
      t('UnreportedDataPointsHome.branchName.label'),
      t('UnreportedDataPointsHome.unansweredPeriodsColumn.label'),
    ];
    const excelRows = [];
    results.forEach((dp) => {
      const code = dp.dataPointCode || dp.code || '';
      const name = dp.name || '';
      const branches = dp.branches || [];
      if (branches.length === 0) {
        excelRows.push([code, name, '', '-']);
      } else {
        branches.forEach((b) => {
          const periods =
            (b.unAnsweredPeriods || []).map(periodLabel).filter(Boolean).join(', ') || '-';
          excelRows.push([code, name, b.name || String(b.branchId ?? ''), periods]);
        });
      }
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
    worksheet['!cols'] = [{ wch: 16 }, { wch: 42 }, { wch: 30 }, { wch: 44 }];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    const stamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Missing_data_points_${stamp}.xlsx`);
  };

  const isSelected = (ids, id) => {
    const n = normId(id);
    return ids.some((x) => normId(x) === n);
  };

  const toggleParent = (id, checked) => {
    const n = normId(id);
    setForm((p) => {
      const cur = p.parentEntityIds || [];
      const next = checked ? [...new Set([...cur, n])] : cur.filter((x) => normId(x) !== n);
      return { ...p, parentEntityIds: next };
    });
  };

  const toggleTopic = (id, checked) => {
    const n = normId(id);
    setForm((p) => {
      const cur = p.topicIds || [];
      const next = checked ? [...new Set([...cur, n])] : cur.filter((x) => normId(x) !== n);
      return { ...p, topicIds: next };
    });
  };

  const toggleChild = (id, checked) => {
    const n = normId(id);
    setForm((p) => {
      const cur = p.childBranchIds || [];
      const next = checked ? [...new Set([...cur, n])] : cur.filter((x) => normId(x) !== n);
      return { ...p, childBranchIds: next };
    });
  };

  const toggleYearlyYear = (yearStr, checked) => {
    setForm((p) => {
      const next = { ...p.period };
      if (checked) next[yearStr] = [];
      else delete next[yearStr];
      return { ...p, period: next };
    });
  };

  const toggleQuarterCode = (yearStr, quarter, checked) => {
    setForm((p) => {
      const next = { ...p.period };
      const cur = [...(next[yearStr] || [])];
      if (checked) {
        if (!cur.includes(quarter)) cur.push(quarter);
        cur.sort((a, b) => QUARTER_CODES.indexOf(a) - QUARTER_CODES.indexOf(b));
      } else {
        const idx = cur.indexOf(quarter);
        if (idx >= 0) cur.splice(idx, 1);
      }
      if (cur.length) next[yearStr] = cur;
      else delete next[yearStr];
      return { ...p, period: next };
    });
  };

  const toggleMonthCode = (yearStr, monthCode, checked) => {
    setForm((p) => {
      const next = { ...p.period };
      const cur = [...(next[yearStr] || [])];
      if (checked) {
        if (!cur.includes(monthCode)) cur.push(monthCode);
        cur.sort((a, b) => MONTH_CODES.indexOf(a) - MONTH_CODES.indexOf(b));
      } else {
        const idx = cur.indexOf(monthCode);
        if (idx >= 0) cur.splice(idx, 1);
      }
      if (cur.length) next[yearStr] = cur;
      else delete next[yearStr];
      return { ...p, period: next };
    });
  };

  const reportingPeriodSelectionCount = useMemo(
    () => countReportingSelections(form.periodType, form.period),
    [form.periodType, form.period]
  );

  const filteredParentEntityRows = useMemo(() => {
    const q = normalizeEntitySearch(parentEntitySearch);
    if (!q) return parentEntityRows;
    return parentEntityRows.filter((r) => (r.name || '').toLowerCase().includes(q));
  }, [parentEntityRows, parentEntitySearch]);

  const filteredChildBranchRows = useMemo(() => {
    const q = normalizeEntitySearch(childBranchSearch);
    if (!q) return childBranchRows;
    return childBranchRows.filter((r) => (r.name || '').toLowerCase().includes(q));
  }, [childBranchRows, childBranchSearch]);

  const parentFilteredAll =
    filteredParentEntityRows.length > 0 &&
    filteredParentEntityRows.every((r) => isSelected(form.parentEntityIds, r.id));
  const childFilteredAll =
    filteredChildBranchRows.length > 0 &&
    filteredChildBranchRows.every((r) => isSelected(form.childBranchIds, r.id));

  const monthLabel = (code) => t(`UnreportedDataPointsHome.month.${code}.label`);

  const parentNone = form.parentEntityIds.length === 0;
  const topicsAll = topics.length > 0 && form.topicIds.length === topics.length;
  const topicsNone = form.topicIds.length === 0;
  const childNone = form.childBranchIds.length === 0;

  const parentUnselectDisabled =
    normalizeEntitySearch(parentEntitySearch).length > 0
      ? !filteredParentEntityRows.some((r) => isSelected(form.parentEntityIds, r.id))
      : parentNone;
  const childUnselectDisabled =
    normalizeEntitySearch(childBranchSearch).length > 0
      ? !filteredChildBranchRows.some((r) => isSelected(form.childBranchIds, r.id))
      : childNone;

  return (
    <div className="unreported-dp-home" data-testid="unreported-dp-home">
      <Card className="unreported-dp-home-card border-0 shadow-sm">
        <Card.Body className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <div>
            <Card.Title className="unreported-dp-home-title mb-1">
              {t('UnreportedDataPointsHome.cardTitle.label')}
            </Card.Title>
            <Card.Text className="text-muted small mb-0">
              {t('UnreportedDataPointsHome.cardDescription.label')}
            </Card.Text>
          </div>
          <Button
            variant="primary"
            className="unreported-dp-home-cta"
            data-testid="unreported-dp-open-button"
            onClick={openModal}
          >
            {t('UnreportedDataPointsHome.button.label')}
          </Button>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => {
          closeAllDropdowns();
          setShowModal(false);
        }}
        size="fullscreen"
        className="unreported-dp-modal"
        data-testid="unreported-dp-modal"
      >
        <Modal.Header className="unreported-dp-modal-header border-bottom">
          <Modal.Title>{t('UnreportedDataPointsHome.modalTitle.label')}</Modal.Title>
          <button
            type="button"
            className="unreported-dp-modal-close btn btn-link p-0"
            aria-label={t('UnreportedDataPointsHome.close.label')}
            onClick={() => {
              closeAllDropdowns();
              setShowModal(false);
            }}
          >
            <AiOutlineClose size={24} />
          </button>
        </Modal.Header>
        <Modal.Body className="unreported-dp-modal-body p-0 d-flex flex-column">
          <div className="unreported-dp-filters border-bottom">
            <div className="unreported-dp-filter-bar">
              <div className="unreported-dp-filter-segment">
                <span className="unreported-dp-filter-label">{t('UnreportedDataPointsHome.category.label')}</span>
                <Form.Select
                  className="unreported-dp-pill-control"
                  value={form.categoryType}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, categoryType: Number(e.target.value), topicIds: [] }))
                  }
                  data-testid="unreported-dp-category"
                >
                  {categoryOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="unreported-dp-filter-segment">
                <span className="unreported-dp-filter-label">{t('UnreportedDataPointsHome.frequency.label')}</span>
                <Form.Select
                  className="unreported-dp-pill-control"
                  value={form.periodType}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === PERIOD_TYPE.YEARLY) {
                      const cy = String(new Date().getFullYear());
                      setForm((p) => ({ ...p, periodType: PERIOD_TYPE.YEARLY, period: { [cy]: [] } }));
                    } else if (next === PERIOD_TYPE.QUARTERLY) {
                      setForm((p) => ({ ...p, periodType: PERIOD_TYPE.QUARTERLY, period: {} }));
                    } else {
                      setForm((p) => ({ ...p, periodType: PERIOD_TYPE.MONTHLY, period: {} }));
                    }
                  }}
                  data-testid="unreported-dp-frequency"
                  aria-label={t('UnreportedDataPointsHome.frequency.label')}
                >
                  <option value={PERIOD_TYPE.YEARLY}>{t('UnreportedDataPointsHome.frequency.yearly.label')}</option>
                  <option value={PERIOD_TYPE.QUARTERLY}>{t('UnreportedDataPointsHome.frequency.quarterly.label')}</option>
                  <option value={PERIOD_TYPE.MONTHLY}>{t('UnreportedDataPointsHome.frequency.monthly.label')}</option>
                </Form.Select>
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--dropdown">
                <div className="ghg-header-pill" data-testid="unreported-dp-reporting-period-dropdown">
                  <Dropdown
                    show={showReportingPeriodDropdown}
                    onToggle={(isOpen) => {
                      if (isOpen) {
                        setShowParentDropdown(false);
                        setShowTopicsDropdown(false);
                        setShowChildDropdown(false);
                      }
                      setShowReportingPeriodDropdown(isOpen);
                    }}
                  >
                    <Dropdown.Toggle className="ghg-dropdown-toggle" id="unreported-dp-dd-reporting-period">
                      {t('UnreportedDataPointsHome.reportingPeriod.dropdown.label')}
                      {reportingPeriodSelectionCount > 0 && ` (${reportingPeriodSelectionCount})`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
                      <div className="ghg-dropdown-scrollable">
                        {form.periodType === PERIOD_TYPE.YEARLY &&
                          fiscalYearOptions.map((y) => {
                            const ys = String(y);
                            const checked = Object.prototype.hasOwnProperty.call(form.period, ys);
                            return (
                              <div key={ys} className="d-flex align-items-start mb-2">
                                <Form.Check
                                  type="checkbox"
                                  id={`unreported-fy-${ys}`}
                                  className="me-2 mt-0"
                                  checked={checked}
                                  onChange={(e) => toggleYearlyYear(ys, e.target.checked)}
                                />
                                <label htmlFor={`unreported-fy-${ys}`} className="mb-0 cursor-pointer small">
                                  <span className="fw-semibold">{ys}</span>
                                  <span className="text-muted ms-1">
                                    {t('UnreportedDataPointsHome.fiscalYear.hint.label', {
                                      startYear: y - 1,
                                      year: y,
                                    })}
                                  </span>
                                </label>
                              </div>
                            );
                          })}

                        {form.periodType === PERIOD_TYPE.QUARTERLY &&
                          fiscalYearOptions.map((y) => {
                            const ys = String(y);
                            return (
                              <div key={ys} className="mb-2">
                                <div className="fw-semibold small mb-1">{ys}</div>
                                {QUARTER_CODES.map((q) => (
                                  <div key={`${ys}-${q}`} className="d-flex align-items-center mb-1 ps-1">
                                    <Form.Check
                                      type="checkbox"
                                      id={`unreported-q-${ys}-${q}`}
                                      className="me-2"
                                      checked={Boolean(form.period[ys]?.includes(q))}
                                      onChange={(e) => toggleQuarterCode(ys, q, e.target.checked)}
                                    />
                                    <label htmlFor={`unreported-q-${ys}-${q}`} className="mb-0 cursor-pointer small">
                                      {ys} — {q}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            );
                          })}

                        {form.periodType === PERIOD_TYPE.MONTHLY &&
                          fiscalYearOptions.map((y) => {
                            const ys = String(y);
                            return (
                              <div key={ys} className="mb-2">
                                <div className="fw-semibold small mb-1">{ys}</div>
                                {MONTH_CODES.map((mcode) => (
                                  <div key={`${ys}-${mcode}`} className="d-flex align-items-center mb-1 ps-1">
                                    <Form.Check
                                      type="checkbox"
                                      id={`unreported-m-${ys}-${mcode}`}
                                      className="me-2"
                                      checked={Boolean(form.period[ys]?.includes(mcode))}
                                      onChange={(e) => toggleMonthCode(ys, mcode, e.target.checked)}
                                    />
                                    <label htmlFor={`unreported-m-${ys}-${mcode}`} className="mb-0 cursor-pointer small">
                                      {ys} — {monthLabel(mcode)}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--check">
                <Form.Check
                  type="checkbox"
                  id="unreported-dp-unanswered"
                  className="unreported-dp-unanswered-check mb-0"
                  label={t('UnreportedDataPointsHome.unanswered.label')}
                  checked={form.unanswered}
                  onChange={(e) => setForm((p) => ({ ...p, unanswered: e.target.checked }))}
                  data-testid="unreported-dp-unanswered"
                />
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--dropdown">
                <div className="ghg-header-pill" data-testid="unreported-dp-topics-dropdown">
                  <Dropdown
                    show={showTopicsDropdown}
                    onToggle={(isOpen) => {
                      if (isOpen) {
                        setShowParentDropdown(false);
                        setShowChildDropdown(false);
                        setShowReportingPeriodDropdown(false);
                      }
                      setShowTopicsDropdown(isOpen);
                    }}
                  >
                    <Dropdown.Toggle className="ghg-dropdown-toggle" id="unreported-dp-dd-topics">
                      {t('UnreportedDataPointsHome.topicIds.dropdown.label')}
                      {form.topicIds.length > 0 && ` (${form.topicIds.length})`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
                      <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary ghg-select-all-btn"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              topicIds: topics.map((x) => normId(x.id)),
                            }))
                          }
                          disabled={topicsAll || topics.length === 0}
                        >
                          {t('SelectAll.label')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                          onClick={() => setForm((p) => ({ ...p, topicIds: [] }))}
                          disabled={topicsNone}
                        >
                          {t('UnselectAll.label')}
                        </button>
                      </div>
                      <div className="ghg-dropdown-scrollable">
                        {topics.map((topic) => (
                          <div key={topic.id} className="d-flex align-items-center mb-1">
                            <Form.Check
                              type="checkbox"
                              id={`unreported-topic-${topic.id}`}
                              checked={isSelected(form.topicIds, topic.id)}
                              onChange={(e) => toggleTopic(topic.id, e.target.checked)}
                              className="me-2"
                            />
                            <label htmlFor={`unreported-topic-${topic.id}`} className="mb-0 cursor-pointer">
                              {topic.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--dropdown">
                <div className="ghg-header-pill" data-testid="unreported-dp-parent-dropdown">
                  <Dropdown
                    show={showParentDropdown}
                    onToggle={(isOpen) => {
                      if (isOpen) {
                        setShowTopicsDropdown(false);
                        setShowChildDropdown(false);
                        setShowReportingPeriodDropdown(false);
                      } else {
                        setParentEntitySearch('');
                      }
                      setShowParentDropdown(isOpen);
                    }}
                  >
                    <Dropdown.Toggle className="ghg-dropdown-toggle" id="unreported-dp-dd-parent">
                      {t('UnreportedDataPointsHome.parentEntity.dropdown.label')}
                      {form.parentEntityIds.length > 0 && ` (${form.parentEntityIds.length})`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
                      <div
                        className="mb-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form.Control
                          type="search"
                          size="sm"
                          className="unreported-dp-entity-search"
                          placeholder={t('Search.label')}
                          value={parentEntitySearch}
                          onChange={(e) => setParentEntitySearch(e.target.value)}
                          aria-label={t('Search.label')}
                          data-testid="unreported-dp-parent-search"
                        />
                      </div>
                      <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary ghg-select-all-btn"
                          onClick={() =>
                            setForm((p) => {
                              const add = filteredParentEntityRows.map((r) => normId(r.id));
                              const cur = p.parentEntityIds || [];
                              return { ...p, parentEntityIds: [...new Set([...cur, ...add])] };
                            })
                          }
                          disabled={filteredParentEntityRows.length === 0 || parentFilteredAll}
                        >
                          {t('SelectAll.label')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                          onClick={() =>
                            setForm((p) => {
                              const cur = p.parentEntityIds || [];
                              if (normalizeEntitySearch(parentEntitySearch).length > 0) {
                                const drop = new Set(filteredParentEntityRows.map((r) => normId(r.id)));
                                return {
                                  ...p,
                                  parentEntityIds: cur.filter((id) => !drop.has(normId(id))),
                                };
                              }
                              return { ...p, parentEntityIds: [] };
                            })
                          }
                          disabled={parentUnselectDisabled}
                        >
                          {t('UnselectAll.label')}
                        </button>
                      </div>
                      <div className="ghg-dropdown-scrollable">
                        {normalizeEntitySearch(parentEntitySearch).length > 0 &&
                        filteredParentEntityRows.length === 0 &&
                        parentEntityRows.length > 0 ? (
                          <p className="text-muted small mb-0 py-1">
                            {t('UnreportedDataPointsHome.entitySearchNoMatch.label')}
                          </p>
                        ) : (
                          filteredParentEntityRows.map((pEnt) => (
                            <div key={pEnt.id} className="d-flex align-items-center mb-1">
                              <Form.Check
                                type="checkbox"
                                id={`unreported-parent-${pEnt.id}`}
                                checked={isSelected(form.parentEntityIds, pEnt.id)}
                                onChange={(e) => toggleParent(pEnt.id, e.target.checked)}
                                className="me-2"
                              />
                              <label htmlFor={`unreported-parent-${pEnt.id}`} className="mb-0 cursor-pointer">
                                {pEnt.name}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--dropdown">
                <div className="ghg-header-pill" data-testid="unreported-dp-branches-dropdown">
                  <Dropdown
                    show={showChildDropdown}
                    onToggle={(isOpen) => {
                      if (isOpen) {
                        setShowParentDropdown(false);
                        setShowTopicsDropdown(false);
                        setShowReportingPeriodDropdown(false);
                      } else {
                        setChildBranchSearch('');
                      }
                      setShowChildDropdown(isOpen);
                    }}
                  >
                    <Dropdown.Toggle className="ghg-dropdown-toggle" id="unreported-dp-dd-child">
                      {t('UnreportedDataPointsHome.childBranchIds.dropdown.label')}
                      {form.childBranchIds.length > 0 && ` (${form.childBranchIds.length})`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
                      <div
                        className="mb-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form.Control
                          type="search"
                          size="sm"
                          className="unreported-dp-entity-search"
                          placeholder={t('Search.label')}
                          value={childBranchSearch}
                          onChange={(e) => setChildBranchSearch(e.target.value)}
                          aria-label={t('Search.label')}
                          data-testid="unreported-dp-child-search"
                        />
                      </div>
                      <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary ghg-select-all-btn"
                          onClick={() =>
                            setForm((p) => {
                              const add = filteredChildBranchRows.map((r) => normId(r.id));
                              const cur = p.childBranchIds || [];
                              return { ...p, childBranchIds: [...new Set([...cur, ...add])] };
                            })
                          }
                          disabled={filteredChildBranchRows.length === 0 || childFilteredAll}
                        >
                          {t('SelectAll.label')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                          onClick={() =>
                            setForm((p) => {
                              const cur = p.childBranchIds || [];
                              if (normalizeEntitySearch(childBranchSearch).length > 0) {
                                const drop = new Set(filteredChildBranchRows.map((r) => normId(r.id)));
                                return {
                                  ...p,
                                  childBranchIds: cur.filter((id) => !drop.has(normId(id))),
                                };
                              }
                              return { ...p, childBranchIds: [] };
                            })
                          }
                          disabled={childUnselectDisabled}
                        >
                          {t('UnselectAll.label')}
                        </button>
                      </div>
                      <div className="ghg-dropdown-scrollable">
                        {normalizeEntitySearch(childBranchSearch).length > 0 &&
                        filteredChildBranchRows.length === 0 &&
                        childBranchRows.length > 0 ? (
                          <p className="text-muted small mb-0 py-1">
                            {t('UnreportedDataPointsHome.entitySearchNoMatch.label')}
                          </p>
                        ) : (
                          filteredChildBranchRows.map((cEnt) => (
                            <div key={cEnt.id} className="d-flex align-items-center mb-1">
                              <Form.Check
                                type="checkbox"
                                id={`unreported-child-${cEnt.id}`}
                                checked={isSelected(form.childBranchIds, cEnt.id)}
                                onChange={(e) => toggleChild(cEnt.id, e.target.checked)}
                                className="me-2"
                              />
                              <label htmlFor={`unreported-child-${cEnt.id}`} className="mb-0 cursor-pointer">
                                {cEnt.name}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="unreported-dp-filter-segment unreported-dp-filter-segment--actions">
                <Button variant="primary" size="sm" onClick={handleApply} data-testid="unreported-dp-apply">
                  {t('UnreportedDataPointsHome.apply.label')}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleReset} data-testid="unreported-dp-reset">
                  {t('UnreportedDataPointsHome.reset.label')}
                </Button>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="unreported-dp-excel-tooltip">{t('DownloadAsExcel.label')}</Tooltip>
                  }
                >
                  <span className="d-inline-block">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDownloadExcel}
                      disabled={results.length === 0}
                      data-testid="unreported-dp-excel"
                      aria-label={t('DownloadAsExcel.label')}
                      className="unreported-dp-excel-icon-btn"
                      style={results.length === 0 ? { pointerEvents: 'none' } : undefined}
                    >
                      <RiFileExcel2Line className="unreported-dp-excel-icon" size={20} aria-hidden />
                    </Button>
                  </span>
                </OverlayTrigger>
              </div>
            </div>
            {fetchError ? (
              <div className="text-danger small px-3 py-2" role="alert" data-testid="unreported-dp-error">
                {fetchError}
              </div>
            ) : null}
          </div>

          <div
            className="unreported-dp-results flex-grow-1 overflow-auto p-3"
            data-testid="unreported-dp-results"
          >
            {results.length === 0 ? (
              <p className="text-muted mb-0">{t('UnreportedDataPointsHome.noResultsYet.label')}</p>
            ) : (
              results.map((dp) => {
                const code = dp.dataPointCode || dp.code;
                const branches = dp.branches || [];
                return (
                  <div key={dp.id} className="unreported-dp-datapoint-block mb-4" data-testid="unreported-dp-datapoint-block">
                    <h6 className="fw-bold mb-2">
                      <span className="text-nowrap me-2">{code}</span>
                      <span>{dp.name}</span>
                    </h6>
                    <Table responsive bordered hover size="sm" className="unreported-dp-table mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t('UnreportedDataPointsHome.branchName.label')}</th>
                          <th>{t('UnreportedDataPointsHome.unansweredPeriodsColumn.label')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {branches.map((b) => (
                          <tr key={b.branchId}>
                            <td>{b.name || b.branchId}</td>
                            <td className="small">
                              {(b.unAnsweredPeriods || [])
                                .map((iso) =>
                                  formatUnansweredPeriodLabel(iso, (c) =>
                                    t(`UnreportedDataPointsHome.month.${c}.label`)
                                  )
                                )
                                .filter(Boolean)
                                .join(', ') || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {branches.length === 0 && (
                      <p className="text-muted small mb-0">{t('UnreportedDataPointsHome.noBranchesForDatapoint.label')}</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default withTranslation()(UnreportedDataPointsHome);
